import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchProjects, createProject } from "../../../api/project";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  XCircleIcon,
  ClockIcon,
  RectangleStackIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { Form, Formik } from "formik";
import { PMField } from "../PMField";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { buildProjectsIndexParams } from "../../../utils/projectsIndexParams";

/**
 * Build GET /projects params with a proper `filters` array (never spread a filters array into an object).
 */
function projectsIndexParams(baseParams, extraFilterRules = [], { activeOnly = true } = {}) {
  return buildProjectsIndexParams(
    { ...baseParams, filters: extraFilterRules },
    { activeOnly }
  );
}

/**
 * Modern Project Selector Component
 * 
 * Features:
 * - Fast project fetching with debounced search
 * - Suggested projects based on recent time registrations
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedProjects - Array of selected project objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of projects)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: true)
 * @param {Array} props.filters - Additional API filter rules `{ name, value }[]` merged into the request
 * @param {boolean} props.includeInactiveProjects - When true, omit default active-only filter
 */
export default function ModernProjectSelector({
  open = false,
  selectedProjects = [],
  onChange,
  onClose,
  multiple = true,
  filters = [],
  /** When true, do not send the default `active: true` filter (inactive projects may appear). */
  includeInactiveProjects = false,
  showHeader = true,
  showSuggestedProjects = true, // Set to false to disable suggested projects and load regular projects instead
}) {
  const currentUser = useSelector((state) => state.session.currentUser);
  const canCreateProjectRole = useUserHasAccess({ role: "projects", action: "create" });
  const canEditProjectsRole = useUserHasAccess({ role: "edit_projects" });
  const canCreateProject = canCreateProjectRole || canEditProjectsRole;
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [suggestedProjects, setSuggestedProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedProjects.map((p) => p.id))
  );
  /** Når true foreslåtte er tomme: vi viser siste N prosjekter i stedet */
  const [idleListIsRecentFallback, setIdleListIsRecentFallback] = useState(false);
  const perPage = 20;
  const recentFallbackPerPage = 10;

  const filtersRef = useRef(filters);
  const includeInactiveRef = useRef(includeInactiveProjects);
  filtersRef.current = filters;
  includeInactiveRef.current = includeInactiveProjects;

  // Update selectedIds when selectedProjects prop changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedProjects.map((p) => p.id)));
    }
  }, [selectedProjects, open]);

  // Load suggested projects based on recent time registrations, or regular projects if disabled
  const loadSuggestedProjects = useCallback(async () => {
    setIsLoadingSuggestions(true);
    setIdleListIsRecentFallback(false);
    try {
      if (!showSuggestedProjects) {
        const projectsResponse = await fetchProjects(
          projectsIndexParams(
            {
              page: 1,
              per_page: 20,
              sortBy: "created_at",
              sort: "desc",
              fields: "id,name,displayTitle,status",
            },
            filtersRef.current,
            { activeOnly: !includeInactiveRef.current }
          )
        );

        if (projectsResponse?.data && Array.isArray(projectsResponse.data)) {
          setSuggestedProjects(projectsResponse.data);
          setTotalCount(projectsResponse.total_count || projectsResponse.data.length);
        } else {
          setSuggestedProjects([]);
        }
        return;
      }

      const loadRecentProjects = async () => {
        const recentResponse = await fetchProjects(
          projectsIndexParams(
            {
              page: 1,
              per_page: recentFallbackPerPage,
              sortBy: "created_at",
              sort: "desc",
              fields: "id,name,displayTitle,status",
            },
            filtersRef.current,
            { activeOnly: !includeInactiveRef.current }
          )
        );
        if (recentResponse?.data && Array.isArray(recentResponse.data)) {
          setSuggestedProjects(recentResponse.data);
          setIdleListIsRecentFallback(true);
        } else {
          setSuggestedProjects([]);
        }
      };

      if (!currentUser?.id) {
        await loadRecentProjects();
        return;
      }

      const projectsResponse = await fetchProjects(
        projectsIndexParams(
          {
            include_suggested: true,
            per_page: 0,
            page: 1,
            fields: "id,name,displayTitle,status",
          },
          filtersRef.current,
          { activeOnly: !includeInactiveRef.current }
        )
      );

      const suggested = projectsResponse?.suggestedProjects;
      if (Array.isArray(suggested) && suggested.length > 0) {
        setSuggestedProjects(suggested);
        setIdleListIsRecentFallback(false);
      } else {
        await loadRecentProjects();
      }
    } catch (err) {
      console.error("Error loading projects:", err);
      setSuggestedProjects([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, showSuggestedProjects]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setShowFilters(false);
      setProjects([]); // Clear previous search results
      // Load suggested projects when dialog opens
      loadSuggestedProjects();
    } else {
      // Reset when closed
      setProjects([]);
      setSuggestedProjects([]);
      setIdleListIsRecentFallback(false);
    }
  }, [open, loadSuggestedProjects]);

  // Create search function that will be debounced
  const searchFunction = useCallback(async (query, page) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchProjects(
        projectsIndexParams(
          {
            query: query || undefined,
            page: page,
            per_page: perPage,
            sortBy: "created_at",
            sort: "desc",
            fields: "id,name,displayTitle,status",
          },
          filtersRef.current,
          { activeOnly: !includeInactiveRef.current }
        )
      );

      if (response && response.data) {
        if (page === 1) {
          setProjects(response.data);
        } else {
          setProjects((prev) => {
            const prevIds = new Set(prev.map((p) => p.id));
            const newRows = response.data.filter((p) => !prevIds.has(p.id));
            return [...prev, ...newRows];
          });
        }

        setTotalCount(response.total_count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setProjects([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Kunne ikke laste prosjekter");
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [perPage]);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction or filters change
  useEffect(() => {
    // Cancel previous debounced calls
    debouncedSearchRef.current.cancel();
    // Create new debounced function
    debouncedSearchRef.current = debounce(searchFunction, 300);
  }, [searchFunction]);

  // Fetch projects when search query changes (only if there's a query)
  useEffect(() => {
    if (!open) {
      // Cancel any pending searches when dialog closes
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    // Only search if there's a query, otherwise just show suggested projects
    if (searchQuery && searchQuery.trim()) {
      setCurrentPage(1);
      debouncedSearchRef.current(searchQuery, 1);
    } else {
      // Clear search results when query is empty
      setProjects([]);
      setIsLoading(false);
      setError(null);
    }

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open]); // Removed filters from dependencies to avoid infinite loop

  // Load more projects
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && searchQuery && searchQuery.trim()) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery, nextPage);
    }
  }, [isLoading, hasMore, currentPage, searchQuery]);

  // Toggle project selection
  const toggleProject = useCallback(
    (project) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(project.id)) {
        newSelectedIds.delete(project.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(project.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected projects - prioritize projects from current list (they have full data)
      const selectedFromCurrent = projects
        .filter((p) => newSelectedIds.has(p.id));
      const selectedFromSuggested = suggestedProjects
        .filter(
          (p) =>
            newSelectedIds.has(p.id) &&
            !projects.find((prod) => prod.id === p.id)
        );
      const selectedFromPrevious = selectedProjects
        .filter(
          (p) =>
            newSelectedIds.has(p.id) &&
            !projects.find((prod) => prod.id === p.id) &&
            !suggestedProjects.find((prod) => prod.id === p.id)
        );
      const selected = [...selectedFromCurrent, ...selectedFromSuggested, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      projects,
      suggestedProjects,
      selectedProjects,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if project is selected
  const isSelected = useCallback(
    (projectId) => {
      return selectedIds.has(projectId);
    },
    [selectedIds]
  );

  // Suggested list when idle; full API search results when querying (including items that are also “often used”)
  const allProjects = useMemo(() => {
    if (searchQuery && searchQuery.trim()) {
      return projects;
    }
    return suggestedProjects;
  }, [projects, suggestedProjects, searchQuery]);

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg prosjekter" : "Velg prosjekt"}
            </h3>
            <div className="flex items-center space-x-2">
              {canCreateProject && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="p-1.5 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                  title="Legg til nytt prosjekt"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter prosjekter..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "prosjekt" : "prosjekter"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter prosjekter..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Project List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {/* Suggested Projects Section */}
        {!searchQuery && suggestedProjects.length > 0 && (
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="px-2 sm:px-4 py-2 border-b border-gray-200">
              <div className="flex items-center gap-2">
                {showSuggestedProjects &&
                  (idleListIsRecentFallback ? (
                    <RectangleStackIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ClockIcon className="h-4 w-4 text-gray-500" />
                  ))}
                <h4 className="text-xs font-semibold text-gray-700 uppercase">
                  {showSuggestedProjects
                    ? idleListIsRecentFallback
                      ? "Siste prosjekter"
                      : "Foreslåtte prosjekter"
                    : "Prosjekter"}
                </h4>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {suggestedProjects
                .slice(0, idleListIsRecentFallback ? recentFallbackPerPage : 20)
                .map((project) => {
                const selected = isSelected(project.id);
                return (
                  <div
                    key={project.id}
                    className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                      selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                    } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                    onClick={!multiple ? () => toggleProject(project) : undefined}
                  >
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProject(project);
                          }}
                          className="focus:outline-none"
                        >
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selected
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300 hover:border-blue-400"
                            }`}
                          >
                            {selected && (
                              <CheckIcon className="h-4 w-4 text-white" />
                            )}
                          </div>
                        </button>
                      </div>

                      {/* Project Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {project.displayTitle || project.name}
                        </p>
                        {project.status && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Status: {project.status}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Results */}
        {isLoadingSuggestions && !searchQuery && suggestedProjects.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">
                {showSuggestedProjects ? "Laster foreslåtte prosjekter..." : "Laster prosjekter..."}
              </p>
            </div>
          </div>
        ) : isLoading && allProjects.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster prosjekter...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : allProjects.length === 0 && !isLoadingSuggestions ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen prosjekter funnet"
                  : suggestedProjects.length === 0
                  ? showSuggestedProjects
                    ? "Ingen foreslåtte prosjekter. Søk etter prosjekter for å begynne."
                    : "Ingen prosjekter. Søk etter prosjekter for å begynne."
                  : "Søk etter prosjekter for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {allProjects.slice(0, 20).map((project) => {
              const selected = isSelected(project.id);
              
              return (
                <div
                  key={project.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleProject(project) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProject(project);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Project Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {project.displayTitle || project.name}
                      </p>
                      {project.status && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Status: {project.status}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && searchQuery && allProjects.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere prosjekter
            </button>
          </div>
        )}

        {isLoading && allProjects.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>

      {/* Create Project Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCreateForm(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Legg til nytt prosjekt
                  </h3>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <Formik
                  initialValues={{
                    name: "",
                    project_number: "",
                    customer_id: null,
                    active: true,
                  }}
                  validationSchema={Yup.object({
                    name: Yup.string().required("Navn er påkrevd"),
                  })}
                  onSubmit={async (values, { setSubmitting, setErrors }) => {
                    setIsCreating(true);
                    try {
                      const response = await createProject(values);
                      if (response && response.id) {
                        toast.success("Prosjekt opprettet!");
                        // Add the new project to the list and select it
                        const newProject = response;
                        setProjects((prev) => [newProject, ...prev]);
                        const newSelectedIds = new Set(selectedIds);
                        if (!multiple) {
                          newSelectedIds.clear();
                        }
                        newSelectedIds.add(newProject.id);
                        setSelectedIds(newSelectedIds);
                        
                        if (onChange) {
                          onChange([newProject]);
                        }
                        
                        setShowCreateForm(false);
                        
                        // Auto-close for single selection
                        if (!multiple && onClose) {
                          setTimeout(() => {
                            onClose();
                          }, 300);
                        }
                      } else {
                        throw new Error("Kunne ikke opprette prosjekt");
                      }
                    } catch (err) {
                      console.error("Error creating project:", err);
                      toast.error("Kunne ikke opprette prosjekt");
                      setErrors({ submit: err.message });
                    } finally {
                      setIsCreating(false);
                      setSubmitting(false);
                    }
                  }}
                >
                  {(formik) => (
                    <Form>
                      <div className="space-y-4">
                        <PMField
                          name="name"
                          label="Navn"
                          type="text"
                          required
                          {...formik}
                        />
                        <PMField
                          name="project_number"
                          label="Prosjektnummer"
                          type="text"
                          {...formik}
                        />
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowCreateForm(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Avbryt
                          </button>
                          <button
                            type="submit"
                            disabled={isCreating || formik.isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isCreating ? "Oppretter..." : "Opprett"}
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

